import { useMemo, useState } from 'react'
import type { Player, Position, TeamPlayer, TeamSelection } from '@/game/types'

interface TeamSelectionOvalProps {
  players: Player[]
  team: TeamSelection
  onChange: (team: TeamSelection) => void
  readOnly?: boolean
}

type TeamGroup = keyof TeamSelection

type Slot = {
  group: TeamGroup
  index: number
  position: Position
  left: number
  top: number
  label: string
}

const slots: Slot[] = [
  { group: 'forwards', index: 0, position: 'F', left: 25, top: 16, label: 'Forward pocket' },
  { group: 'forwards', index: 1, position: 'F', left: 50, top: 12, label: 'Full forward' },
  { group: 'forwards', index: 2, position: 'F', left: 75, top: 16, label: 'Forward pocket' },
  { group: 'halfForwards', index: 0, position: 'HF', left: 24, top: 31, label: 'Half-forward flank' },
  { group: 'halfForwards', index: 1, position: 'HF', left: 50, top: 28, label: 'Centre half-forward' },
  { group: 'halfForwards', index: 2, position: 'HF', left: 76, top: 31, label: 'Half-forward flank' },
  { group: 'centre', index: 0, position: 'C', left: 19, top: 47, label: 'Wing' },
  { group: 'centre', index: 1, position: 'C', left: 50, top: 44, label: 'Centre' },
  { group: 'centre', index: 2, position: 'C', left: 81, top: 47, label: 'Wing' },
  { group: 'followers', index: 0, position: 'FOL', left: 38, top: 55, label: 'Ruck' },
  { group: 'followers', index: 1, position: 'FOL', left: 50, top: 57, label: 'Rover' },
  { group: 'followers', index: 2, position: 'FOL', left: 62, top: 55, label: 'Ruck-rover' },
  { group: 'halfBacks', index: 0, position: 'HB', left: 24, top: 68, label: 'Half-back flank' },
  { group: 'halfBacks', index: 1, position: 'HB', left: 50, top: 71, label: 'Centre half-back' },
  { group: 'halfBacks', index: 2, position: 'HB', left: 76, top: 68, label: 'Half-back flank' },
  { group: 'backs', index: 0, position: 'FB', left: 25, top: 84, label: 'Back pocket' },
  { group: 'backs', index: 1, position: 'FB', left: 50, top: 87, label: 'Full back' },
  { group: 'backs', index: 2, position: 'FB', left: 75, top: 84, label: 'Back pocket' },
]

const secondaryGroups: Array<{ group: TeamGroup; title: string; position: Position }> = [
  { group: 'interchange', title: 'Interchange', position: 'INT' },
  { group: 'emergencies', title: 'Emergencies', position: 'EMG' },
]

const cloneTeam = (team: TeamSelection): TeamSelection => ({
  backs: [...team.backs],
  halfBacks: [...team.halfBacks],
  centre: [...team.centre],
  halfForwards: [...team.halfForwards],
  forwards: [...team.forwards],
  followers: [...team.followers],
  interchange: [...team.interchange],
  emergencies: [...team.emergencies],
})

const locatePlayer = (team: TeamSelection, playerId: string) => {
  for (const group of Object.keys(team) as TeamGroup[]) {
    const index = team[group].findIndex(entry => entry.playerId === playerId)
    if (index >= 0) return { group, index }
  }
  return null
}

const positionForSlot = (group: TeamGroup, index: number): Position => {
  const fieldSlot = slots.find(slot => slot.group === group && slot.index === index)
  if (fieldSlot) return fieldSlot.position
  return group === 'interchange' ? 'INT' : 'EMG'
}

export function TeamSelectionOval({ players, team, onChange, readOnly = false }: TeamSelectionOvalProps) {
  const [selectedPlayerId, setSelectedPlayerId] = useState<string | null>(null)
  const [selectionMessage, setSelectionMessage] = useState('')
  const playersById = useMemo(() => new Map(players.map(player => [player.id, player])), [players])
  const selectedIds = useMemo(
    () => new Set((Object.values(team) as TeamPlayer[][]).flat().map(entry => entry.playerId)),
    [team],
  )
  const reserves = players.filter(player => !selectedIds.has(player.id))

  const movePlayer = (playerId: string, targetGroup: TeamGroup, targetIndex: number) => {
    if (readOnly) return
    const unavailable = playersById.get(playerId)?.injury
    if (unavailable && targetGroup !== 'emergencies') {
      setSelectionMessage(`${playersById.get(playerId)?.name} is unavailable with a ${unavailable.severity} ${unavailable.type} (${unavailable.remainingWeeks} ${unavailable.remainingWeeks === 1 ? 'week' : 'weeks'} remaining).`)
      setSelectedPlayerId(null)
      return
    }
    const next = cloneTeam(team)
    const source = locatePlayer(next, playerId)
    const target = next[targetGroup][targetIndex]

    if (source?.group === targetGroup && source.index === targetIndex) {
      setSelectedPlayerId(null)
      return
    }

    const movingPlayer = source ? next[source.group][source.index] : undefined
    const movingEntry: TeamPlayer = {
      playerId,
      position: positionForSlot(targetGroup, targetIndex),
      role: movingPlayer?.role ?? 'standard',
    }

    if (source) {
      if (target) {
        next[source.group][source.index] = {
          ...target,
          position: positionForSlot(source.group, source.index),
        }
      } else {
        next[source.group].splice(source.index, 1)
      }
    }

    next[targetGroup][targetIndex] = movingEntry
    const unavailableSenior = (Object.entries(next) as [TeamGroup, TeamPlayer[]][])
      .filter(([group]) => group !== 'emergencies')
      .flatMap(([, entries]) => entries)
      .map(entry => playersById.get(entry.playerId))
      .find(player => player?.injury)
    if (unavailableSenior?.injury) {
      setSelectionMessage(`${unavailableSenior.name} is unavailable with a ${unavailableSenior.injury.severity} ${unavailableSenior.injury.type} (${unavailableSenior.injury.remainingWeeks} ${unavailableSenior.injury.remainingWeeks === 1 ? 'week' : 'weeks'} remaining).`)
      setSelectedPlayerId(null)
      return
    }
    onChange(next)
    setSelectionMessage('Team selection updated.')
    setSelectedPlayerId(null)
  }

  const startDrag = (event: React.DragEvent, playerId: string) => {
    event.dataTransfer.setData('text/player-id', playerId)
    event.dataTransfer.effectAllowed = 'move'
    setSelectedPlayerId(playerId)
  }

  const acceptDrop = (event: React.DragEvent, group: TeamGroup, index: number) => {
    event.preventDefault()
    const playerId = event.dataTransfer.getData('text/player-id') || selectedPlayerId
    if (playerId) movePlayer(playerId, group, index)
  }

  const playerButton = (entry: TeamPlayer | undefined, group: TeamGroup, index: number, label: string, className = '') => {
    const player = entry ? playersById.get(entry.playerId) : undefined
    const suitability = player && player.position !== positionForSlot(group, index) && !['INT', 'EMG'].includes(positionForSlot(group, index))
      ? 'Position suitability uncertain'
      : 'Natural or staff-approved role'

    return (
      <button
        key={`${group}-${index}-${entry?.playerId ?? 'empty'}`}
        type="button"
        className={`oval-player-slot ${entry ? 'occupied' : 'empty'} ${selectedPlayerId === entry?.playerId ? 'selected' : ''} ${className}`}
        draggable={Boolean(entry) && !readOnly}
        onDragStart={event => entry && startDrag(event, entry.playerId)}
        onDragOver={event => event.preventDefault()}
        onDrop={event => acceptDrop(event, group, index)}
        onClick={() => {
          if (selectedPlayerId) movePlayer(selectedPlayerId, group, index)
          else if (entry) {
            if (player?.injury && group !== 'emergencies') {
              setSelectedPlayerId(entry.playerId)
              setSelectionMessage(`${player.name} is unavailable. Select a healthy emergency or available squad player to replace him.`)
            } else if (player?.injury) {
              setSelectionMessage(`${player.name} is unavailable with a ${player.injury.severity} ${player.injury.type} (${player.injury.remainingWeeks} ${player.injury.remainingWeeks === 1 ? 'week' : 'weeks'} remaining).`)
            } else setSelectedPlayerId(entry.playerId)
          }
        }}
        data-tooltip={player
          ? `${label} | ${player.name} | ${player.primaryRole} | Fitness ${player.fitness}, form ${player.form}, morale ${player.morale}. ${suitability}. Drag, or tap then tap another position.`
          : `${label} | Empty position. Drag a player here, or tap a player then tap this position.`}
        aria-label={player ? `${label}: ${player.name}` : `${label}: empty`}
      >
        <span>{player ? player.name.split(' ').at(-1) : positionForSlot(group, index)}</span>
        {player && <small>{player.injury ? `${player.injury.severity} injury · ${player.injury.remainingWeeks}w` : entry?.role}</small>}
      </button>
    )
  }

  return (
    <div className="selection-layout">
      <p className="selection-message" role="status" aria-live="polite">{selectionMessage || 'Injured players are unavailable for senior selection.'}</p>
      <section className="oval-board" aria-label="Australian rules team selection oval">
        <div className="field-marking centre-square" />
        <div className="field-marking centre-circle" />
        <div className="field-marking arc top" />
        <div className="field-marking arc bottom" />
        {slots.map(slot => (
          <div
            className={`field-slot-anchor ${slot.left >= 65 ? 'tooltip-left' : slot.left <= 35 ? 'tooltip-right' : 'tooltip-centre'}`}
            key={`${slot.group}-${slot.index}`}
            style={{ left: `${slot.left}%`, top: `${slot.top}%` }}
          >
            {playerButton(team[slot.group][slot.index], slot.group, slot.index, slot.label)}
          </div>
        ))}
      </section>

      <aside className="selection-bench">
        {secondaryGroups.map(({ group, title, position }) => (
          <section key={group} className="selection-group">
            <h2>{title}<span>{team[group].length}</span></h2>
            <div className="bench-list">
              {team[group].map((entry, index) => playerButton(entry, group, index, `${title} ${index + 1}`, 'bench-slot'))}
              <button
                type="button"
                className="oval-player-slot empty bench-slot"
                onDragOver={event => event.preventDefault()}
                onDrop={event => acceptDrop(event, group, team[group].length)}
                onClick={() => selectedPlayerId && movePlayer(selectedPlayerId, group, team[group].length)}
                data-tooltip={`Add to ${title.toLowerCase()}. Drag or tap a selected player here.`}
              >
                <span>+ {position}</span>
              </button>
            </div>
          </section>
        ))}

        <section className="selection-group reserves">
          <h2>Available squad<span>{reserves.length}</span></h2>
          <div className="bench-list">
            {reserves.map(player => (
              <button
                type="button"
                className={`oval-player-slot reserve-slot ${selectedPlayerId === player.id ? 'selected' : ''}`}
                key={player.id}
                draggable={!readOnly && !player.injury}
                onDragStart={event => !player.injury && startDrag(event, player.id)}
                onClick={() => player.injury
                  ? setSelectionMessage(`${player.name} is unavailable with a ${player.injury.severity} ${player.injury.type} (${player.injury.remainingWeeks} ${player.injury.remainingWeeks === 1 ? 'week' : 'weeks'} remaining).`)
                  : setSelectedPlayerId(selectedPlayerId === player.id ? null : player.id)}
                data-tooltip={`${player.name} | ${player.primaryRole} | Fitness ${player.fitness}, form ${player.form}, morale ${player.morale}. Drag onto the oval.`}
              >
                <span>{player.name}</span>
                <small>{player.injury ? `${player.injury.severity} ${player.injury.type} · ${player.injury.remainingWeeks}w` : `${player.position} · Fit ${player.fitness}`}</small>
              </button>
            ))}
          </div>
        </section>
      </aside>
    </div>
  )
}
